import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@repo/api-client";
import type { CommunityStory, StorySubmissionRequest } from "@repo/shared-types";

const CATEGORIES = ["All", "relationships", "mental-health", "dreams", "grief", "interfaith", "nature", "healing"];

export default function StoriesPage() {
  const [stories, setStories] = useState<CommunityStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { stories: nextStories } = await api.listStories({ category: category !== "All" ? category : undefined });
      setStories(nextStories);
      setLoading(false);
    })();
  }, [category]);

  const featuredStories = stories.filter((story) => story.is_featured);
  const regularStories = stories.filter((story) => !story.is_featured);

  return (
    <div className="cap-page">
      <section className="cap-hero">
        <div className="container">
          <div className="cap-hero__shell cap-hero__shell--split">
            <div>
              <span className="cap-hero__badge">Community Stories</span>
              <h1 className="display-1 cap-hero__title">Real stories of healing and awakening</h1>
              <p className="cap-hero__subtitle">
                Shared by the community with love and courage, these stories bring lived transformation into view.
              </p>
              <div className="hero-actions">
                <button onClick={() => setShowForm(true)} className="btn btn--gold">Share your story</button>
                <Link to="/library" className="btn btn--outline">Browse the library</Link>
              </div>
            </div>

            <aside className="cap-hero__aside">
              <div className="cap-hero__card">
                <div className="cap-stat-grid">
                  <div className="cap-stat"><span className="cap-stat__value">{stories.length || 18}</span><span className="cap-stat__label">stories</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">{featuredStories.length || 4}</span><span className="cap-stat__label">featured</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">8</span><span className="cap-stat__label">categories</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">1</span><span className="cap-stat__label">submission path</span></div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="section section--surface-alt">
        <div className="container">
          <div className="cap-toolbar" style={{ marginBottom: "1.75rem" }}>
            <div className="cap-filter-row">
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCategory(c)} className={`cap-chip ${category === c ? "cap-chip--active" : ""}`}>
                  {c.replace("-", " ")}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <p className="text-muted body-lg text-center" style={{ padding: "4rem 0" }}>Loading stories…</p>
          ) : stories.length === 0 ? (
            <p className="text-muted body-lg text-center" style={{ padding: "4rem 0" }}>No stories found.</p>
          ) : (
            <div style={{ display: "grid", gap: "2rem" }}>
              {featuredStories.length > 0 && (
                <section className="cap-panel">
                  <div className="cap-panel__header"><h2 className="heading-lg">Featured stories</h2></div>
                  <div className="cap-panel__body">
                    <div className="cap-grid cap-grid--stories">
                      {featuredStories.map((story) => <StoryCard key={story.id} story={story} featured />)}
                    </div>
                  </div>
                </section>
              )}

              <section className="cap-panel">
                <div className="cap-panel__header"><h2 className="heading-lg">All stories</h2></div>
                <div className="cap-panel__body">
                  <div className="cap-grid cap-grid--stories">
                    {regularStories.map((story) => <StoryCard key={story.id} story={story} />)}
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      </section>

      {showForm && <StorySubmitModal onClose={() => setShowForm(false)} />}
    </div>
  );
}

function StoryCard({ story, featured = false }: { story: CommunityStory; featured?: boolean }) {
  return (
    <Link to={`/stories/${story.slug}`} className={`cap-card ${featured ? "" : ""}`}>
      <div className="cap-card__media" style={{ minHeight: "13rem" }}>
        {story.featured_image_url ? <img src={story.featured_image_url} alt={story.title} /> : <span aria-hidden="true">💛</span>}
      </div>
      <div className="cap-card__body">
        <div className="cap-card__meta-row">
          {featured && <span className="cap-pill cap-pill--gold">Featured</span>}
          <span className="cap-pill cap-pill--indigo">{story.category.replace("-", " ")}</span>
        </div>
        <h3 className="heading-lg">{story.title}</h3>
        <p className="text-muted body-sm">By {story.author_name}</p>
        <div className="cap-card__meta-row" style={{ marginTop: "auto", color: "var(--c-muted)", fontSize: "0.82rem" }}>
          <span>{story.read_time_minutes} min read</span>
          {story.like_count > 0 && <span>💛 {story.like_count}</span>}
        </div>
      </div>
    </Link>
  );
}

function StorySubmitModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState<StorySubmissionRequest>({
    title: "",
    author_name: "",
    author_email: "",
    body: "",
    category: "",
    tags: [],
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  function update(field: keyof StorySubmissionRequest, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    await api.submitStory(form);
    setLoading(false);
    setSubmitted(true);
  }

  return (
    <div className="cap-story-modal">
      <div className="cap-story-modal__sheet">
        <div className="cap-story-modal__header">
          <h2 className="heading-lg">Share your story</h2>
          <button onClick={onClose} className="btn btn--ghost">Close</button>
        </div>
        <div className="cap-story-modal__body">
          {submitted ? (
            <div style={{ textAlign: "center", padding: "2rem 0" }}>
              <div className="display-3">🙏</div>
              <h3 className="heading-lg" style={{ marginTop: "1rem" }}>Thank you for sharing</h3>
              <p className="text-muted body-sm" style={{ marginTop: "0.5rem" }}>Your story has been submitted for review. We read every submission with care.</p>
              <button onClick={onClose} className="btn btn--gold" style={{ marginTop: "1.5rem" }}>Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: "grid", gap: "1rem" }}>
              <div className="form-group">
                <label className="form-label">Story title *</label>
                <input value={form.title} onChange={(e) => update("title", e.target.value)} required minLength={5} placeholder="A meaningful title for your story" className="form-input" />
              </div>
              <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                <div className="form-group">
                  <label className="form-label">Your name *</label>
                  <input value={form.author_name} onChange={(e) => update("author_name", e.target.value)} required className="form-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" value={form.author_email ?? ""} onChange={(e) => update("author_email", e.target.value)} required className="form-input" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Brief bio (optional)</label>
                <input value={form.bio ?? ""} onChange={(e) => update("bio", e.target.value)} placeholder="A line or two about yourself" className="form-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select value={form.category} onChange={(e) => update("category", e.target.value)} required className="form-input">
                  <option value="">Select a category</option>
                  {CATEGORIES.slice(1).map((category) => <option key={category} value={category}>{category.replace("-", " ")}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Your story * (min 100 characters)</label>
                <textarea value={form.body} onChange={(e) => update("body", e.target.value)} required minLength={100} rows={10} placeholder="Share your story of transformation, healing, or awakening…" className="form-input" />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button type="submit" disabled={loading} className="btn btn--gold">{loading ? "Submitting…" : "Submit story"}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
